import React from 'react';
import { 
  RocketLaunchIcon, 
  DocumentTextIcon, 
  UserGroupIcon, 
  LightBulbIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  HeartIcon,
  DocumentIcon,
  PresentationChartLineIcon,
  ChartBarIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { Link } from 'react-router-dom';

const TransparencyCharterStage2 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection 
        title="Stage 2: Transparency Charter" 
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />} 
        heading
      >
        <div className="bg-gray-800/30 p-6 rounded-lg border border-gray-700/50">
          <h3 className="text-xl font-semibold text-white mb-4">TL;DR</h3>
          
          <ExpandableCallout 
            type="info" 
            title="What do I need to DO in this stage?"
            defaultOpen={true}
          >
            <p className="text-gray-300 mb-4">You need to do 2 things:</p>
            <ol className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1</span>
                <span className="text-gray-300">Get the Exec / Leadership team together for a workshop (half-day)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2</span>
                <span className="text-gray-300">Design & Document your Transparency Charter as a team</span>
              </li>
            </ol>
            <p className="text-gray-300 mt-4">Here's what we mean…</p>
          </ExpandableCallout>
        </div>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <UserGroupIcon className="h-6 w-6 text-primary-light mr-3" />
            Building Your Transparency Charter Together
          </h3>
          <div className="prose prose-invert max-w-none">
            <h4 id="action" className="text-lg font-medium text-white mt-6">Action:</h4>
            <p className="text-gray-300">
              In the last stage you got buy-in from leadership to build this. Now it's time to do it, together. You need to be looking for 3 outcomes from your workshop:
            </p>
            
            <ul className="mt-4 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Identify your 'why' (Why is transparency important here?)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Map out where your opportunities are to be transparent (e.g. hiring, finances, diversity)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Identify which areas you want to lean into more (e.g. for brand or accountability)</span>
              </li>
            </ul>
            
            <h4 className="text-lg font-medium text-white mt-6 flex items-start">
              <span className="text-primary-light font-bold mr-2">1</span>
              Your 'Why'
            </h4>
            
            <div className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <p className="text-gray-300">
                You need to anchor transparency to something to give it some meaning in your company. You can't simply put words like transparent, open, honest on a careers page or as a value without showing people 'why' it's there. Some common things you might anchor it to:
              </p>
            </div>
            
            <ul className="mt-4 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <span className="italic text-gray-300">Your product</span>
                  <span className="text-gray-300"> - Maybe you're bringing 'transparency' to a certain market or industry via your product, and therefore transparency plays a big part in your GTM. It naturally follows that it should play a part in how you run your own business.</span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <span className="italic text-gray-300">Founder backstory or principles</span>
                  <span className="text-gray-300"> - Does this have personal meaning to your founder? If so, why? Tell that story, and bring that word to life.</span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <span className="italic text-gray-300">It's a core value</span>
                  <span className="text-gray-300">, but for a reason. You might be a remote business that relies on transparency to communicate and collaborate at pace. You might be Open-Source.</span>
                </div>
              </li>
            </ul>
            
            <p className="text-gray-300 mt-4">
              Find your 'why'. It's vital to shaping what transparency actually means in your company.
            </p>
            
            <p className="text-gray-300 mt-4">Example:</p>
          </div>
          
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/transparency-charter/posthog-tc.png"
            alt="PostHog transparency example with clear 'why' for transparency"
            className="mt-4 rounded-lg border border-gray-700/50"
            caption="PostHog's transparency charter"
          />
          
          <ExpandableCallout 
            type="info" 
            title="PostHog Example"
            className="mt-6"
          >
            <p className="text-gray-300">
              <strong>PostHog</strong> have a clear 'why' for transparency as a business. It links heavily to one of their core-values, and aligns heavily with their product & strategy too.
            </p>
            <p className="text-gray-300 mt-2">
              It's useful to note too that they provide clear examples of how they live & breathe transparency as a business too, and the 'why' behind those specific choices (e.g. High-performance culture & 'how we do our best work together)
            </p>
          </ExpandableCallout>
          
          <div className="prose prose-invert max-w-none mt-8">
            <h4 className="text-lg font-medium text-white flex items-start">
              <span className="text-primary-light font-bold mr-2">2</span>
              Map out Opportunity
            </h4>
            
            <div className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <p className="text-gray-300">
                There are so many touchpoints and opportunities to build trust with clarity across the 'people journey', both CX and PX. Understanding where those touchpoints are will help you understand what you could share, and why, and whether there are also things at those potential touch points that you can't share too. Documenting and agreeing on what you don't share is just as important.
              </p>
            </div>
            
            <p className="text-gray-300 mt-4">Some examples of things that might be relevant:</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
              <div className="flex items-center">
                <UserGroupIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Diversity</span>
              </div>
              <div className="flex items-center">
                <BriefcaseIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Hiring</span>
              </div>
              <div className="flex items-center">
                <AcademicCapIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Onboarding</span>
              </div>
              <div className="flex items-center">
                <BanknotesIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Finances</span>
              </div>
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Compensation</span>
              </div>
              <div className="flex items-center">
                <TrophyIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Career Development</span>
              </div>
              <div className="flex items-center">
                <HeartIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Culture</span>
              </div>
              <div className="flex items-center">
                <LightBulbIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Decision-Making</span>
              </div>
              <div className="flex items-center">
                <DocumentIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Documentation</span>
              </div>
              <div className="flex items-center">
                <RocketLaunchIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Strategy & Vision</span>
              </div>
              <div className="flex items-center">
                <ChartBarIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">People Metrics</span>
              </div>
              <div className="flex items-center">
                <PresentationChartLineIcon className="h-5 w-5 text-primary-light mr-2 flex-shrink-0" />
                <span className="text-gray-300">Business Performance</span>
              </div>
            </div>
            
            <p className="text-gray-300 mt-4">
              Don't worry about potentially missing out something here, this will grow and evolve, and you will have the opportunity to also throw it out to the team upon completion, which may also unearth new areas to think about. Some things that might help guide you on where there could be opportunity to share information though…
            </p>
            
            <ul className="mt-4 space-y-2">
              <li className="flex items-start">
                <div className="mt-2">
                  <ToolCallout 
                    tool="notion"
                    label="The Open Culture Framework"
                    href="https://www.notion.so/The-Open-Culture-Framework-f6e6d2bedd3b42a38da92b1637b47305"
                    description="35 areas across CX/PX companies could be transparent"
                  />
                </div>
              </li>
              <li className="flex items-start">
                <div className="mt-2">
                  <p className="text-gray-300 mb-2">
                    Gitlab's handbook resources on transparency:
                  </p>
                  <div className="space-y-2">
                    <ToolCallout 
                      label="Gitlab Transparency Values"
                      href="https://handbook.gitlab.com/handbook/values/#transparency"
                      description="What they share and why"
                      tool="gitlab"
                    />
                    <ToolCallout 
                      label="Gitlab Confidentiality Levels"
                      href="https://handbook.gitlab.com/handbook/communication/confidentiality-levels/#not-public"
                      description="What they don't share and why"
                      tool="gitlab"
                    />
                  </div>
                </div>
              </li>
            </ul>
            
            <div className="flex items-start mt-4">
              <span className="text-primary-light mr-2">—</span>
              <p className="text-gray-300">
                As an outcome here you want to aim to cover as many of these areas as possible and agree on:
              </p>
            </div>
            
            <ul className="mt-2 pl-8 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Things specifically that could be shared</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <span className="text-gray-300">Which ones you do and don't share, and why</span>
                  <ul className="mt-2 pl-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300 italic">e.g. For 'Finances' you may identify the opportunity to potentially share monthly revenue, P+L, burn rate and runway. But, you may opt to only share revenue with the team for now because there's some education work needed first to help folks understand runway and burn rate first.</span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
            
            <div className="flex items-start mt-6">
              <span className="text-primary-light mr-2">—</span>
              <p className="text-gray-300">
                <strong>Workshop Tip:</strong> How you achieve the above is up to you, but there's often a lot to cover, so thinking about some asynchronous pre-work beforehand could be worthwhile. Possibly a mapping exercise yourself to identify as many of the broad areas as possible so you are one step ahead on the day, but also asking the team to throw in more granular suggestions within those buckets before the day too. You can then spend the time agreeing on what is and isn't shared based on general sentiment rather than spending the workshop time identifying it all.
              </p>
            </div>
            
            <ul className="mt-4 pl-8 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300 italic">E.g. You might put the above 16 areas on a Miro board and ask everyone to throw in as many ideas / suggestions under each one before the day (e.g. 'Diversity' might have things like 'leadership team stats/make up, wider team make up, commitments, initiatives, failings added underneath it)</span>
              </li>
            </ul>
            
            <p className="text-gray-300 mt-4">Example:</p>
          </div>
          
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/transparency-charter/tc-miro.png"
            alt="Example of using a sliding scale to gauge transparency appetite"
            className="mt-4 rounded-lg border border-gray-700/50"
          />
          
          <p className="text-gray-300 mt-4">
            To get a feel for general appetite or sentiment you could use a sliding scale on Miro and ask everyone to pick somewhere on that slider. Usually gives you a good feel for general appetite for sharing certain things, and additionally helps you understand 'how' open you should be about something.
          </p>
          
          <div className="prose prose-invert max-w-none mt-8">
            <h4 className="text-lg font-medium text-white flex items-start">
              <span className="text-primary-light font-bold mr-2">3</span>
              Identify the areas you want to lean into
            </h4>
            
            <div className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <p className="text-gray-300">
                You obviously want to establish a baseline level of transparency across as many areas of your business as possible; that will help to generally decide 'how' open you are as a company. However, it's worth thinking about whether there are certain areas you could over-index on and lean into when it comes to transparency. There's probably two reasons you might do this:
              </p>
            </div>
            
            <ul className="mt-4 space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <span className="text-gray-300">
                    <strong>It elevates your brand & compliments your culture;</strong> e.g. if one of your values is 'Strength in Diversity' it could be argued that you'd benefit from leaning as much as possible into making diversity something that you are incredibly open & honest about. Look at your values, your brand, and identify whether there are things you say, do or talk about that might be elevated and reinforced by showing more transparency there.
                  </span>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <span className="text-gray-300">
                    <strong>You need the extra accountability there to either maintain or improve;</strong> e.g. again taking diversity as an example, if this is a particular challenge for you, it may help you to lean more into some level of public vulnerability as way of driving some improvement and extra accountability
                  </span>
                </div>
              </li>
            </ul>
            
            <p className="text-gray-300 mt-4">
              If you get this right, it can work wonders for your business, not just because the additional visibility & accountability genuinely improves health in that area, but because it can really help you to stand out from an employer brand perspective.
            </p>
            
            <p className="text-gray-300 mt-4"><strong>Examples:</strong></p>
            <ul className="mt-2 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300"><strong>Buffer</strong> are well known for their salary transparency dashboard</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300"><strong>Help Scout</strong> are well known for their DEI dashboard</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300"><strong>Posthog</strong> are well known for their public product roadmap</span>
              </li>
            </ul>
          </div>
          
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/transparency-charter/buffer-salaries.png"
            alt="Buffer's salary transparency dashboard"
            className="mt-4 rounded-lg border border-gray-700/50"
          />
          <p className="text-sm text-gray-400 text-center mt-1">Buffer</p>
          
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/transparency-charter/by-gender.png"
            alt="Help Scout's DEI dashboard"
            className="mt-4 rounded-lg border border-gray-700/50"
          />
          <p className="text-sm text-gray-400 text-center mt-1">Help Scout</p>
          
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/transparency-charter/posthog-t-roadmap.png"
            alt="Posthog's public product roadmap"
            className="mt-4 rounded-lg border border-gray-700/50"
          />
          <p className="text-sm text-gray-400 text-center mt-1">Posthog</p>
          
          <p className="text-gray-300 mt-4">
            If appropriate, find something that you can lean into that you become well known for sharing that perhaps others dont. It can be a powerful brand differentiator.
          </p>
          
          <ExpandableCallout 
            type="warning" 
            title="Be Cautious"
            className="mt-6"
          >
            <p className="text-gray-300">
              The most important thing here is to make it appropriate, healthy, achievable and embeddable. If you can't maintain & sustain it, then you shouldn't commit to it.
            </p>
          </ExpandableCallout>
        </PlaybookCard>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-primary-light mr-3" />
            Document It
          </h3>
          
          <div className="prose prose-invert max-w-none">
            <h4 id="action-document" className="text-lg font-medium text-white mt-4">Action:</h4>
            
            <p className="text-gray-300">
              Now you've hopefully workshopped the above, you want to get it documented and down in writing. There's no right or wrong way necessarily to do this, but our advice would be to make it as accessible as possible (ideally for the outside world too, so that applicants can see it).
            </p>
            
            <p className="text-gray-300 mt-4">
              We like how Gitlab do it, although they are incredibly detailed and thorough so this might be too lengthy for most companies, but high-level on their handbook they:
            </p>
            
            <ul className="mt-4 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Make it clear they 'default to public' (and why, with examples)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Make it clear that there are some things that aren't public, but they are documented</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Within that documentation, they are clear about what is Internal (not public), but also what is also restricted internally (only for certain eyes). They provide examples, and 'why' too.</span>
              </li>
            </ul>
            
        
            
            <div className="mt-6 space-y-4">
              <p className="text-gray-300">
                Here's a ready to use notion template to get you going. 
              </p>
              
              <ToolCallout 
                tool="notion"
                label="Transparency Charter Template"
                href="https://www.notion.so/Transparency-Charter-Template-8144239bba1f448c9a6155750f903955"
                description="A ready-to-use template for your transparency charter"
              />
            </div>
            
            <p className="text-gray-300 mt-6">
              If you happen to decide you don't want to share the entire charter documentation publicly, we'd advise as a bare minimum you can find a way of sharing something on job ads, handbooks or career site which covers in a paragraph:
            </p>
            
            <ul className="mt-4 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Why transparency</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">What it does, and doesnt mean</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">An example or two of things you do share, but also don't share and why</span>
              </li>
            </ul>
          </div>
          
          <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
            <p className="text-gray-300">
              After completing this stage, proceed to <Link to="/playbooks/transparency-charter/stage-3" className="text-primary hover:text-primary-light">Stage 3: Engage, Embed & Reinforce</Link> where you'll share and operationalize your transparency charter.
            </p>
          </div>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
};

export default TransparencyCharterStage2; 